// SkuBuilder — compose a SKU from its parts instead of typing a string.
//
// WHY THIS EXISTS
// `NewItemModal` had a free-text SKU box with a placeholder and one check: does
// it start FF- or SD-. Everything after the prefix was whatever somebody typed,
// which is how a catalog fills up with FF-AP-2-CH next to FF-AP-02-1-CH next to
// FF-AUTOPILOT-REV2. Meanwhile `PurchaseOrderForm` has had a proper segment
// builder since chunk 1 — same grammar, enforced, one screen away.
//
// So this is that builder, lifted out so both surfaces use it. Nothing here is
// new behavior; it is the PO form's control with its state made into props.
//
// ── TWO SHAPES, BECAUSE THE CONVENTION HAS TWO ──────────────────────────────
// BOARDS are strict: {BRAND}-{BOARD}-{GEN}-{REV}[-{REV2}][-V{N}]-{VENDOR}[-P].
// Every segment is a decision somebody made (a re-spin, a second assembler, a
// stuffing variant) and the backend PARSES it to resolve a part family, so a
// malformed one silently loses its family.
//
// EVERYTHING ELSE is loose by convention, but the live catalog settled on
// {BRAND}-{TYPE}-{GEN}-{N} anyway. So the non-board side still composes rather
// than free-types: brand and generation are pickers, and only the type token and
// tail are typed. That is the smallest amount of freehand that can still express
// a part nobody anticipated.
//
// The preview under the field is the whole point of the control: you watch the
// string assemble as you answer, so the format is learned rather than
// memorized.
//
// ── THE BOARD LIST IS DATA ──────────────────────────────────────────────────
// It used to be BOARD_CODES, a frozen array, so a board type nobody anticipated
// was a deploy. It is the `item_kinds` table now (the kinds whose code_mode is
// `structured`), cached in stores/useItemKindsStore. The last option in the
// picker adds one without leaving this form.

import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../apiHelpers';
import ItemKindForm from '../Admin/ItemKindForm';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import { Alert } from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { Checkbox } from '../components/ui/Checkbox';
import { Field, fieldClass } from '../components/ui/Field';
import { Radio, RadioGroup } from '../components/ui/Radio';
import { Select } from '../components/ui/Select';
import { Tooltip } from '../components/ui/Tooltip';
import useItemKindsStore, { structuredKinds, type ItemKind } from '../stores/useItemKindsStore';
import {
  BRAND_PREFIXES,
  BOARD_SKU_REGEX,
  CURRENT_GENERATION,
  DEFAULT_BRAND,
  previewBoardSku,
  type Brand,
  type BoardFields,
} from './skuGrammar';

export type SkuShape = 'board' | 'non_board';

export interface NonBoardFields {
  brand: Brand;
  /** The 2-4 char type token: CAB, SCR, MTR. Typed, because the list is open. */
  typeToken: string;
  generation: string;
  /** Numeric tail, 1-based within the stem. */
  tail: string;
}

export const emptyBoard = (): BoardFields => ({
  brand: DEFAULT_BRAND,
  board: 'AP',
  generation: CURRENT_GENERATION,
  revMajor: '',
  revMinor: '',
  variant: '',
  prototype: false,
  vendorCode: '',
});

export const emptyNonBoard = (): NonBoardFields => ({
  brand: DEFAULT_BRAND,
  typeToken: '',
  generation: CURRENT_GENERATION,
  tail: '1',
});

/** The composed string for the loose shape. Mirrors suggestItemSku's stem. */
export function previewNonBoardSku(f: NonBoardFields): string {
  const token = f.typeToken.trim().toUpperCase();
  if (!token) return '';
  const segs = [f.brand, token, f.generation || CURRENT_GENERATION];
  const tail = f.tail.trim();
  return tail ? `${segs.join('-')}-${tail}` : segs.join('-');
}

/** Is the composed string one the backend will take? */
export function skuIsWellFormed(shape: SkuShape, sku: string): boolean {
  if (!sku) return false;
  if (shape === 'board') return BOARD_SKU_REGEX.test(sku);
  // Loose shape: BRAND-TYPE-GENERATION-TAIL, all four present.
  //
  // The tail used to be optional here, because `(-[A-Z0-9]+)+` is satisfied by
  // the generation segment alone. previewNonBoardSku emits BRAND-TYPE-GEN with
  // no tail when the field is blank, so FF-SCR-02 passed validation and could
  // be minted — a SKU with no unit number, which the numbering scheme has no
  // way to extend and skuTailMax cannot read.
  //
  // It bit on multi-line purchase orders specifically: PurchaseOrderForm clears
  // the tail after each added line, so line 1 minted FF-SCR-02-1 correctly and
  // line 2 minted FF-SCR-02.
  //
  // The warning copy has claimed a number was required all along. This makes
  // the claim true. [A-Z0-9]+ rather than digits only, because variant tails
  // like FF-DST-02-LG are deliberate.
  return /^(FF|SD)-[A-Z0-9]{2,}-[A-Z0-9]+-[A-Z0-9]+$/.test(sku);
}

interface VendorOption {
  id: string;
  code: string | null;
  name: string;
  /** Retired shops are not offered, but a code already on the SKU names them. */
  is_disabled?: boolean;
}

/** The row that clears the segment. `''` cannot be an option id: the combobox reads it as "nothing chosen". */
const CLEAR_VENDOR = '__clear_vendor__';

interface Props {
  shape: SkuShape;
  onShapeChange: (s: SkuShape) => void;
  board: BoardFields;
  onBoardChange: (b: BoardFields) => void;
  nonBoard: NonBoardFields;
  onNonBoardChange: (f: NonBoardFields) => void;
  /** Vendors with a two-letter code, for the manufacturer segment: any type,
   *  not only board houses, because the backend resolves the segment by code
   *  alone. The segment is always a picker; a code the list does not carry
   *  (a parsed guess, a retired shop) stays visible as the current value, and
   *  the caller's own check says whether it names anyone. */
  codedVendors?: VendorOption[];
  /** Passed by callers so the control inherits each surface's label style. */
  labelClass: string;
  /**
   * The Board segment is already decided, so show it and do not offer it again.
   *
   * For the New Item wizard, where step one IS the question "what kind of thing
   * is this" and step two composes the code for the answer. Re-asking it here
   * would be a second control for one decision, and the two could disagree.
   *
   * It hides the shape radios for the same reason: a locked board kind means
   * the board grammar, so "A board / Anything else" has no answer left to give.
   */
  lockBoard?: boolean;
  /**
   * Offer "New vendor…" as the pinned last row of the manufacturer segment.
   *
   * Same offer as the Board picker's "New type…" one segment to the left, and
   * for the same standing rule: a picker whose value does not exist yet offers
   * to create it in place. Absent means the row is not rendered, which is
   * what a surface with nowhere to put a vendor form wants.
   */
  onNewVendor?: () => void;
}

// One segment: a tiny centered label over a narrow box. The label is small on
// purpose — the boxes are the content, and eight full-size labels would put the
// wall straight back.
const Seg: React.FC<{ label: string; width: string; optional?: boolean; children: React.ReactNode }> = ({
  label,
  width,
  optional,
  children,
}) => (
  <div className={width}>
    <span className={`block text-[10px] uppercase tracking-wider mb-1 text-center ${optional ? 'text-on-surface-variant' : 'text-on-surface-variant'}`}>
      {label}
    </span>
    {children}
  </div>
);

// The literal dash from the SKU, sitting between the boxes at the same baseline.
// Faded when the segment after it is empty, so the string's real shape shows
// through the control.
const Dash: React.FC<{ faded?: boolean }> = ({ faded }) => (
  <span className={`pb-2 font-mono select-none ${faded ? 'text-outline' : 'text-on-surface-variant'}`} aria-hidden="true">
    -
  </span>
);

// The sentinel value on the Board picker's last option. A picker whose value
// does not exist yet offers to create it in place, per the standing rule in
// web/CLAUDE.md: nobody abandons a half-filled item to go and visit an admin
// page. The kind lands straight back in this field.
const NEW_KIND = '__new__';

// The same sentinel on the manufacturer segment, for the same reason. A row
// rather than a second control beside the field, and pinned past the filter the
// way KindPicker's is: an assembler nobody has entered yet is the common case
// on a first board from a new shop, so the offer has to survive the query that
// found nothing.
const NEW_VENDOR = '__new_vendor__';

// What an option says. The Board box is narrow on purpose (the row is a picture
// of the SKU), so a kind's name only rides along when the pair still fits;
// otherwise the key stands alone and the name is the option's tooltip.
const OPTION_TEXT_BUDGET = 12;
function optionText(kind: ItemKind): string {
  const pair = `${kind.key} \u00b7 ${kind.label}`;
  return kind.label && pair.length <= OPTION_TEXT_BUDGET ? pair : kind.key;
}

// The kit field at segment width. Every segment (the selects, the text boxes, the locked Board
// div, the manufacturer combobox) shares this shape so the row stays a picture of the SKU: a
// segment that sized itself differently would put a step in the string. The `!` beats the
// well's own side padding, which the narrow boxes cannot afford.
const SEG = '!px-1.5 text-center font-mono';
const segClass = `${fieldClass({ size: 'md' })} ${SEG}`;

const BRAND_OPTIONS = BRAND_PREFIXES.map((p) => ({ key: p, label: p }));

const SkuBuilder: React.FC<Props> = ({
  shape,
  onShapeChange,
  board,
  onBoardChange,
  nonBoard,
  onNonBoardChange,
  codedVendors = [],
  labelClass,
  lockBoard = false,
  onNewVendor,
}) => {
  const preview = useMemo(
    () => (shape === 'board' ? previewBoardSku(board) : previewNonBoardSku(nonBoard)),
    [shape, board, nonBoard]
  );
  const wellFormed = skuIsWellFormed(shape, preview);

  // The board vocabulary is a table now (item_kinds), not a constant in
  // skuGrammar. Subscribe to the raw array and derive: calling the store's own
  // structuredKinds() inside a selector returns a fresh array every render and
  // re-renders forever.
  const api = useApi();
  const kinds = useItemKindsStore((s) => s.kinds);
  const loadingKinds = useItemKindsStore((s) => s.loading);
  const kindsError = useItemKindsStore((s) => s.error);
  const loadKinds = useItemKindsStore((s) => s.load);
  const [addingKind, setAddingKind] = useState(false);
  const boardKinds = useMemo(() => structuredKinds(kinds), [kinds]);
  // The current value always has an option, even before the kinds land or when it names a kind
  // that has since been retired. Without it the selector would show blank over a SKU that says
  // otherwise. A kind whose name did not fit beside its key carries the name as a hint line.
  const boardOptions = useMemo(() => {
    const rows = boardKinds.map((k) => {
      const text = optionText(k);
      return { key: k.key, label: text, hint: text === k.key && k.label ? k.label : undefined };
    });
    if (board.board && !boardKinds.some((k) => k.key === board.board)) {
      rows.unshift({ key: board.board, label: board.board, hint: undefined });
    }
    return rows;
  }, [boardKinds, board.board]);

  // The manufacturer segment's rows: the code is the label, because the label
  // is what the field reads once a row is taken and the row has to collapse to
  // the two letters that go in the string. The name is the sublabel, which the
  // combobox searches by default, so typing "green" finds GC.
  const vendorOptions = useMemo<ComboboxOption[]>(() => {
    const rows: ComboboxOption[] = [];
    const seen = new Set<string>();
    for (const v of codedVendors) {
      const code = (v.code || '').toUpperCase();
      if (!code || v.is_disabled || seen.has(code)) continue;
      seen.add(code);
      rows.push({ id: code, label: code, sublabel: v.name });
    }
    // The current value always has a row, as the Board segment always has an
    // option: a parsed guess or a retired assembler's code would otherwise show
    // as blank over a SKU that says otherwise, and the caller's own check is
    // what says whether it names anyone.
    if (board.vendorCode && !seen.has(board.vendorCode)) {
      const retired = codedVendors.find((v) => (v.code || '').toUpperCase() === board.vendorCode);
      rows.push({
        id: board.vendorCode,
        label: board.vendorCode,
        sublabel: retired ? `${retired.name}, retired` : 'Not in the vendor list',
      });
    }
    if (board.vendorCode) {
      rows.push({ id: CLEAR_VENDOR, label: '··', sublabel: 'No maker yet' });
    }
    if (onNewVendor) {
      rows.push({
        id: NEW_VENDOR,
        label: 'New vendor…',
        sublabel: 'For an assembler nobody has entered yet',
        pinned: true,
      });
    }
    return rows;
  }, [codedVendors, board.vendorCode, onNewVendor]);

  // Cached for the session, and deduplicated in the store, so the two modals
  // that mount this control do not both fetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadKinds(api); }, []);

  return (
    <div className="space-y-3">
      {/* Which grammar. Board first because it is the strict one and the one
          that loses a part family when it is wrong.

          Not asked at all when the board is locked: the caller already knows
          the kind, and a kind knows its own grammar. */}
      {!lockBoard && (
        <div>
          <RadioGroup
            name="sku-shape"
            label="What kind of SKU"
            layout="row"
            value={shape}
            onChange={(next) => onShapeChange(next as SkuShape)}
          >
            {(['board', 'non_board'] as SkuShape[]).map((sh) => (
              <Radio key={sh} value={sh} label={sh === 'board' ? 'A board' : 'Anything else'} />
            ))}
          </RadioGroup>
        </div>
      )}

      <FadeIn key={shape}>
      {shape === 'board' ? (
        // ONE LINE, SHAPED LIKE THE SKU. Narrow boxes separated by literal
        // dashes, in the order they appear in the string, so the control is a
        // picture of its own output. A 3-column grid of full-width fields
        // (which is what the PO form does) hides that relationship completely.
        // Wraps on narrow screens rather than scrolling.
        <div className="flex flex-wrap items-end gap-x-1.5 gap-y-3">
          <Seg label="Brand" width="w-[74px]">
            <Select
              label="Brand"
              value={board.brand}
              onChange={(key) => onBoardChange({ ...board, brand: key as Brand })}
              options={BRAND_OPTIONS}
            />
          </Seg>
          <Dash />
          <Seg label="Board" width="w-[110px]">
            {lockBoard ? (
              // Shown, not offered. The segment still has to appear, because the
              // row is a picture of the SKU and a gap where a segment belongs
              // reads as a field somebody forgot. Not a control and not a tab
              // stop: there is nothing here to change.
              <div className={`${segClass} truncate leading-8`}>{board.board}</div>
            ) : (
            <Tooltip label={kindsError || ''}>
              <Select
                label="Board"
                value={board.board}
                onChange={(key) => onBoardChange({ ...board, board: key })}
                placeholder={loadingKinds ? 'Loading…' : kindsError ? 'Unavailable' : 'Not set'}
                options={boardOptions}
                actions={[{ key: NEW_KIND, label: 'New type…', onSelect: () => setAddingKind(true) }]}
              />
            </Tooltip>
            )}
          </Seg>
          <Dash />
          <Seg label="Gen" width="w-[52px]">
            <Field
              className={SEG}
              aria-label="Generation"
              value={board.generation}
              maxLength={2}
              onChange={(e) =>
                onBoardChange({ ...board, generation: e.target.value.replace(/\D/g, '').slice(0, 2) })
              }
              placeholder={CURRENT_GENERATION}
            />
          </Seg>
          <Dash />
          <Seg label="Maj" width="w-[48px]">
            <Field
              className={SEG}
              aria-label="Rev major"
              value={board.revMajor}
              onChange={(e) => onBoardChange({ ...board, revMajor: e.target.value.replace(/\D/g, '') })}
              placeholder="2"
            />
          </Seg>
          <Dash faded={!board.revMinor} />
          <Seg label="Min" width="w-[48px]" optional>
            <Field
              className={SEG}
              aria-label="Rev minor"
              value={board.revMinor}
              onChange={(e) => onBoardChange({ ...board, revMinor: e.target.value.replace(/\D/g, '') })}
              placeholder="—"
            />
          </Seg>
          <Dash faded={!board.variant} />
          <Seg label="Variant" width="w-[60px]" optional>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-on-surface-variant pointer-events-none">V</span>
              <Field
                className={`${SEG} !pl-[18px]`}
                aria-label="Variant"
                value={board.variant}
                onChange={(e) => onBoardChange({ ...board, variant: e.target.value.replace(/\D/g, '') })}
                placeholder="—"
              />
            </div>
          </Seg>
          <Dash />
          <Seg label="Mfr" width="w-[110px]">
            {/* A picker, always. There used to be a typed two-letter fallback
                for an empty list, reachable only when no caller offered "New
                vendor…"; every caller does now, so the branch was dead and a
                code typed with nobody behind it is exactly what the callers'
                "is nobody's code" check exists to refuse.

                The shared combobox rather than a select, because two letters
                are not a vendor to anybody who has not memorized the table: the
                open list reads the NAME under each code, and the chosen row
                collapses back to the code alone so the row stays a picture of
                the SKU string. Wider than the other narrow segments for the
                same reason a Board segment is: a name needs the pixels while
                the list is open. */}
            <EntityCombobox
              label="Mfr code"
              labelClassName="sr-only"
              inputClassName={segClass}
              placeholder="··"
              value={board.vendorCode}
              options={vendorOptions}
              emptyMessage={onNewVendor ? undefined : 'No maker by that code.'}
              noOptionsMessage="No coded vendors yet."
              onSelect={(o) => {
                if (!o || o.id === CLEAR_VENDOR) { onBoardChange({ ...board, vendorCode: '' }); return; }
                if (o.id === NEW_VENDOR) { if (onNewVendor) onNewVendor(); return; }
                onBoardChange({ ...board, vendorCode: String(o.id) });
              }}
              // Two typed letters that ARE a code take it without Enter, so
              // type-then-Tab keeps working as it did on the select.
              onQueryChange={(q) => {
                const typed = q.trim().toUpperCase();
                if (typed.length === 2 && vendorOptions.some((o) => o.id === typed)) {
                  onBoardChange({ ...board, vendorCode: typed });
                }
              }}
            />
          </Seg>
          <Checkbox
            className="pb-2 pl-2 whitespace-nowrap"
            checked={board.prototype}
            onChange={(e) => onBoardChange({ ...board, prototype: e.target.checked })}
            label={<><span className="font-mono">-P</span> proto</>}
          />
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-x-1.5 gap-y-3">
          <Seg label="Brand" width="w-[74px]">
            <Select
              label="Brand"
              value={nonBoard.brand}
              onChange={(key) => onNonBoardChange({ ...nonBoard, brand: key as Brand })}
              options={BRAND_OPTIONS}
            />
          </Seg>
          <Dash />
          <Seg label="Type" width="w-[76px]">
            <Field
              className={SEG}
              aria-label="Type"
              value={nonBoard.typeToken}
              maxLength={4}
              onChange={(e) =>
                onNonBoardChange({
                  ...nonBoard,
                  typeToken: e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4),
                })
              }
              placeholder="CAB"
            />
          </Seg>
          <Dash />
          <Seg label="Gen" width="w-[52px]">
            <Field
              className={SEG}
              aria-label="Generation"
              value={nonBoard.generation}
              maxLength={2}
              onChange={(e) =>
                onNonBoardChange({ ...nonBoard, generation: e.target.value.replace(/\D/g, '').slice(0, 2) })
              }
              placeholder={CURRENT_GENERATION}
            />
          </Seg>
          <Dash />
          <Seg label="No." width="w-[52px]">
            <Field
              className={SEG}
              aria-label="Number"
              value={nonBoard.tail}
              onChange={(e) =>
                onNonBoardChange({ ...nonBoard, tail: e.target.value.replace(/[^A-Za-z0-9]/g, '') })
              }
              placeholder="1"
            />
          </Seg>
          <p className="text-[11px] text-on-surface-variant pb-2 pl-2">
            Type is 2-4 letters: CAB, SCR, MTR.
          </p>
        </div>
      )}
      </FadeIn>

      {/* THE PREVIEW IS THE TEACHING. Watching the string assemble is how
          somebody learns the format without reading the convention doc. */}
      <div
        className={`rounded-lg px-3 py-2 transition-colors duration-150 ease-emphasized motion-reduce:transition-none ${
          preview && wellFormed
            ? 'bg-primary/10 ring-1 ring-inset ring-primary/40'
            : 'bg-surface-container-low'
        }`}
      >
        <span className={labelClass}>This SKU</span>
        <p className="font-mono text-lg text-on-surface tabular-nums">
          {preview || <span className="text-on-surface-variant text-sm font-sans">Fill in the parts above</span>}
        </p>
        <Reveal show={!!preview && !wellFormed}>
          <Alert tone="warning" variant="text" live={false} className="mt-1 text-[11px]">
            {shape === 'board'
              ? 'Not a complete board SKU yet. Rev major and manufacturer code are both needed.'
              : 'Needs a type and a number.'}
          </Alert>
        </Reveal>
      </div>

      {addingKind && (
        <ItemKindForm
          defaultMode="structured"
          lockMode
          onClose={() => setAddingKind(false)}
          onCreated={(kind) => {
            // Straight into the field that asked for it. The store already has
            // it, so the option is there by the time this renders. Only a board
            // type can sit in this segment; the form locks the mode, and this
            // guard is the belt to that brace.
            if (kind.code_mode === 'structured') onBoardChange({ ...board, board: kind.key });
            setAddingKind(false);
          }}
        />
      )}
    </div>
  );
};

export default SkuBuilder;
